import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ContractStatusEnum } from '@zaoblako/shared/modules/document/enums/contract-status.enum';
import { CompanyFindDto, type CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { ContragentFindDto, type ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { DirectionFindDto, type DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { UnitFindDto, type UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { FilialFindDto, type FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import { CostCenterFindDto, type CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';
import { DocumentFileTemplateFindDto, type DocumentFileTemplateFindInterface } from '@zaoblako/shared/modules/catalog/dtos/document-file-template/document-file-template-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { DocumentDtoKeysEnum } from '@zaoblako/shared/modules/document/enums/document-dto-keys.enum';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import * as yup from 'yup';

/** Contract response body */
export interface ContractFindInterface {
  /** Unique identifier */
  id: number;
  // Tech columns
  /** Creation date and time */
  created: Date;
  /** Last updating date and time */
  updated: Date;
  /** Soft deletion date and time */
  deleted?: Date;
  /** Contract creator user */
  createdBy?: UserFindInterface;
  // Data columns
  /** Contract display name */
  name: string;
  /** Contract expiration date and time */
  expiresAt?: Date;
  /** Is contract expired? This column will be computed automatically. */
  expired?: boolean;
  /** Related company */
  company: CompanyFindInterface;
  /** Related counterparty */
  contragent: ContragentFindInterface;
  /** Related direction */
  direction?: DirectionFindInterface;
  /** Related unit */
  unit?: UnitFindInterface;
  /** Related filial */
  filial?: FilialFindInterface;
  /** Related cost center */
  costCenter?: CostCenterFindInterface;
  /** Related deal */
  deal?: DealFindInterface;
  /** Contract number */
  number: string;
  /** Contract beginning date */
  date?: Date;
  /** 1C ERP database reference code */
  erp1cRef?: string;
  /** If `true` then related documents will be excluded from analytics */
  ignoreInAnalytics?: boolean;
  /** Intergroup turnover flag, used as an additional filter in some contract-related reports */
  intergroupTurnover?: boolean;
  /** Has contract synchronized with company's 1C ERP database */
  erp1cSynchronized: boolean;
  /** Contract current status */
  status: ContractStatusEnum;
  /** Related document file template */
  documentFileTemplate?: DocumentFileTemplateFindInterface;
}

/** Contract response body - DTO schema */
export const ContractFindDto = SerdeDtoClass.create<ContractFindInterface>(DocumentDtoKeysEnum.CONTRACT_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Изменен',
    serializer: yup.date(),
  },
  deleted: {
    label: 'Удален',
    optional: true,
    serializer: yup.date(),
  },
  createdBy: {
    label: 'Автор',
    optional: true,
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  name: {
    label: 'Название',
    sortable: true,
    serializer: yup.string(),
  },
  expiresAt: {
    optional: true,
    sortable: true,
    label: 'Срок действия',
    serializer: yup.date(),
  },
  expired: {
    optional: true,
    label: 'Просрочен',
    serializer: yup.boolean(),
  },
  company: {
    sortable: true,
    label: 'Компания',
    nested: CompanyFindDto,
    serializer: (_, nested) => nested,
  },
  contragent: {
    sortable: true,
    label: 'Контрагент',
    nested: ContragentFindDto,
    serializer: (_, nested) => nested,
  },
  direction: {
    sortable: true,
    optional: true,
    label: 'БС',
    nested: DirectionFindDto,
    serializer: (_, nested) => nested,
  },
  unit: {
    sortable: true,
    optional: true,
    label: 'БЕ',
    nested: UnitFindDto,
    serializer: (_, nested) => nested,
  },
  filial: {
    sortable: true,
    optional: true,
    label: 'Филиал',
    nested: FilialFindDto,
    serializer: (_, nested) => nested,
  },
  costCenter: {
    sortable: true,
    optional: true,
    label: 'Центр затрат',
    nested: CostCenterFindDto,
    serializer: (_, nested) => nested,
  },
  deal: {
    sortable: true,
    optional: true,
    label: 'Сделка',
    nested: BusinessDtoKeysEnum.DEAL_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  number: {
    label: 'Номер',
    sortable: true,
    serializer: yup.string(),
  },
  date: {
    sortable: true,
    label: 'Дата начала договора',
    optional: true,
    serializer: yup.date(),
  },
  erp1cRef: {
    label: 'Внутренний ID в 1С',
    optional: true,
    serializer: yup.string(),
  },
  ignoreInAnalytics: {
    sortable: true,
    optional: true,
    label: 'Не учитывать документы в аналитике',
    serializer: yup.boolean(),
  },
  intergroupTurnover: {
    sortable: true,
    optional: true,
    label: 'Межгрупповой оборот',
    serializer: yup.boolean(),
  },
  erp1cSynchronized: {
    label: 'Синхронизирован с 1С',
    serializer: yup.boolean(),
  },
  status: {
    sortable: true,
    label: 'Текущий статус',
    serializer: yup
      .string()
      .oneOf(Object.values(ContractStatusEnum)),
  },
  documentFileTemplate: {
    sortable: true,
    optional: true,
    label: 'Шаблон документа',
    nested: DocumentFileTemplateFindDto,
    serializer: (_, nested) => nested,
  },
});
